// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	SettingsTabContainer,
	SettingsTabContent,
	SettingsTabSection,
} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {Message} from '@app/features/channel/components/Message';
import {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {GuildMember} from '@app/features/member/models/GuildMember';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {Message as MessageModel} from '@app/features/messaging/models/Message';
import {cdnUrl} from '@app/features/messaging/utils/UrlUtils';
import {SubsectionTitle} from '@app/features/user/components/modals/tabs/component_gallery_tab/ComponentGalleryTabSubsectionTitle';
import styles from '@app/features/user/components/modals/tabs/component_gallery_tab/MessagesTab.module.css';
import {ChannelTypes, MessagePreviewContext, MessageTypes} from '@app/vendor/constants/ChannelConstants';
import {MS_PER_HOUR} from '@app/vendor/date_utils/DateConstants';
import type {MessageEmbed} from '@app/vendor/schema/domains/message/EmbedSchemas';
import type {
	MessageAttachment,
	Message as MessageType,
} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import type {UserPartial} from '@app/vendor/schema/domains/user/UserResponseSchemas';
import {Plural, Trans, useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useEffect, useMemo} from 'react';

const MOCK_GUILD_ID = '1000000000000000001';
const MOCK_CHANNEL_ID = '1000000000000000002';

const MOCK_TIMED_OUT_USER_ID = '1000000000000000003';

let mockIdCounter = 0;

const generateMockId = () => String(Date.now() * 1000 + mockIdCounter++);
const createMockUser = (overrides?: Partial<UserPartial>): UserPartial => ({
	id: overrides?.id ?? generateMockId(),
	username: overrides?.username ?? 'MockUser',
	discriminator: '0000',
	global_name: null,
	avatar: null,
	avatar_color: null,
	bot: overrides?.bot ?? false,
	system: overrides?.system ?? false,
	flags: 0,
});
const createMockChannel = (): Channel =>
	new Channel({
		id: MOCK_CHANNEL_ID,
		type: ChannelTypes.GUILD_TEXT,
		guild_id: MOCK_GUILD_ID,
		name: 'mock-channel',
	});
const createMockMessage = (overrides?: Partial<MessageType>): MessageModel =>
	new MessageModel(
		{
			id: overrides?.id ?? generateMockId(),
			channel_id: MOCK_CHANNEL_ID,
			guild_id: MOCK_GUILD_ID,
			author: overrides?.author ?? createMockUser(),
			type: overrides?.type ?? MessageTypes.DEFAULT,
			content: overrides?.content ?? 'Hello, world!',
			timestamp: overrides?.timestamp ?? new Date().toISOString(),
			embeds: overrides?.embeds ?? [],
			attachments: overrides?.attachments ?? [],
			flags: overrides?.flags ?? 0,
			pinned: overrides?.pinned ?? false,
			mention_everyone: overrides?.mention_everyone ?? false,
		},
		{skipUserCache: true},
	);
const ATTACHMENT_IMAGE_PATHS = [
	'avatars/0.png',
	'avatars/1.png',
	'avatars/2.png',
	'avatars/3.png',
	'avatars/4.png',
	'avatars/5.png',
	'web/android-chrome-192x192.png',
	'web/android-chrome-512x512.png',
	'web/apple-touch-icon.png',
	'web/og-image-default.png',
];
const createMockAttachments = (count: number): Array<MessageAttachment> =>
	Array.from({length: count}, (_, i) => {
		const assetPath = ATTACHMENT_IMAGE_PATHS[i % ATTACHMENT_IMAGE_PATHS.length];
		const url = cdnUrl(assetPath);
		return {
			id: generateMockId(),
			filename: `image-${i + 1}.png`,
			content_type: 'image/png',
			size: 1024,
			url,
			proxy_url: url,
			width: 400,
			height: 300,
			flags: 0,
		};
	});
const createMockRichEmbed = (): MessageEmbed => ({
	id: generateMockId(),
	type: 'rich',
	title: 'Example Rich Embed',
	description: 'This embed demonstrates fields, author, timestamp, and thumbnail.',
	color: 0x3b82f6,
	url: 'https://example.com',
	timestamp: new Date().toISOString(),
	author: {
		name: 'Embed Author',
		url: 'https://example.com',
	},
	fields: [
		{name: 'Field 1', value: 'Inline field', inline: true},
		{name: 'Field 2', value: 'Another inline field', inline: true},
		{
			name: 'Field 3',
			value: 'Full-width field showing longer content inside a single block.',
			inline: false,
		},
	],
	footer: {text: 'Footer text here'},
	thumbnail: {
		url: cdnUrl('avatars/2.png'),
		proxy_url: cdnUrl('avatars/2.png'),
		width: 80,
		height: 80,
		flags: 0,
	},
});
interface MessageDisplayProps {
	label: string;
	message: MessageModel;
	channel: Channel;
	isCompact: boolean;
}
const READONLY_MESSAGE_BEHAVIOR = {
	disableContextMenu: true,
	disableContextMenuTracking: true,
	isHighlight: false,
	isReplying: false,
	contextMenuOpen: false,
} as const;

const MessageDisplay: React.FC<MessageDisplayProps> = ({label, message, channel, isCompact}) => (
	<div className={styles.previewCard}>
		<div className={styles.previewHeader}>
			<div className={styles.messageLabel}>{label}</div>
			<div className={styles.previewModeLabel}>{isCompact ? <Trans>Compact</Trans> : <Trans>Cozy</Trans>}</div>
		</div>
		<div className={styles.previewBody}>
			<Message
				channel={channel}
				message={message}
				previewContext={MessagePreviewContext.SETTINGS}
				readonlyPreview={true}
				removeTopSpacing={true}
				compact={isCompact}
				idPrefix="component-gallery-message-preview"
				behaviorOverrides={{
					...READONLY_MESSAGE_BEHAVIOR,
					messageDisplayCompact: isCompact,
				}}
			/>
		</div>
	</div>
);
export const MessagesTab: React.FC = () => {
	const {t} = useLingui();
	const mockChannel = useMemo(() => createMockChannel(), []);
	useEffect(() => {
		Channels.handleChannelCreate({channel: mockChannel.toJSON()});
		const timedOutUser = createMockUser({id: MOCK_TIMED_OUT_USER_ID, username: 'TimedOutUser'});
		const timedOutMember = new GuildMember(MOCK_GUILD_ID, {
			user: timedOutUser,
			roles: [],
			joined_at: new Date().toISOString(),
			communication_disabled_until: new Date(Date.now() + MS_PER_HOUR).toISOString(),
		});
		GuildMembers.members[MOCK_GUILD_ID] = {
			...GuildMembers.members[MOCK_GUILD_ID],
			[timedOutMember.user.id]: timedOutMember,
		};
		return () => {
			Channels.handleChannelDelete({channel: mockChannel.toJSON()});
			delete GuildMembers.members[MOCK_GUILD_ID];
		};
	}, [mockChannel]);
	const singleLineMessage = useMemo(() => createMockMessage({content: 'This is a single line message.'}), []);
	const multiLineMessage = useMemo(
		() =>
			createMockMessage({
				content:
					'This is a multi-line message.\nLine two demonstrates wrapping.\nLine three makes sure vertical spacing is visible.',
			}),
		[],
	);
	const markdownMessage = useMemo(
		() =>
			createMockMessage({
				content: '**Bold**, *italic*, `inline code`, ~~strikethrough~~, and ||spoiler||.',
			}),
		[],
	);
	const regularUserMessage = useMemo(
		() =>
			createMockMessage({
				author: createMockUser({username: 'RegularUser'}),
				content: 'Message from a regular user.',
			}),
		[],
	);
	const botUserMessage = useMemo(
		() =>
			createMockMessage({
				author: createMockUser({username: 'BotUser', bot: true}),
				content: 'Message from a bot user.',
			}),
		[],
	);
	const systemUserMessage = useMemo(
		() =>
			createMockMessage({
				author: createMockUser({username: 'SystemUser', bot: true, system: true}),
				content: 'Message from a system user.',
			}),
		[],
	);
	const timedOutUserMessage = useMemo(
		() =>
			createMockMessage({
				author: createMockUser({id: MOCK_TIMED_OUT_USER_ID, username: 'TimedOutUser'}),
				content: 'Message from a timed out user (clock icon should appear).',
			}),
		[],
	);
	const richEmbedMessage = useMemo(
		() =>
			createMockMessage({
				content: 'Message with a rich embed:',
				embeds: [createMockRichEmbed()],
			}),
		[],
	);
	const attachmentMessages = useMemo(
		() =>
			Array.from({length: 10}, (_, i) => ({
				count: i + 1,
				message: createMockMessage({
					content: '',
					attachments: createMockAttachments(i + 1),
				}),
			})),
		[],
	);
	return (
		<SettingsTabContainer>
			<SettingsTabContent>
				<SettingsTabSection
					title={<Trans>Display Modes</Trans>}
					description={<Trans>Cozy and compact text variations.</Trans>}
				>
					<div className={styles.displayModeRow}>
						<div className={styles.displayModeColumn}>
							<div className={styles.displayModeHeader}>
								<Trans>Cozy Mode</Trans>
							</div>
							<MessageDisplay
								label={t`Single line`}
								message={singleLineMessage}
								channel={mockChannel}
								isCompact={false}
							/>
							<MessageDisplay
								label={t`Multi-line`}
								message={multiLineMessage}
								channel={mockChannel}
								isCompact={false}
							/>
							<MessageDisplay label={t`Markdown`} message={markdownMessage} channel={mockChannel} isCompact={false} />
						</div>
						<div className={styles.displayModeColumn}>
							<div className={styles.displayModeHeader}>
								<Trans>Compact Mode</Trans>
							</div>
							<MessageDisplay
								label={t`Single line`}
								message={singleLineMessage}
								channel={mockChannel}
								isCompact={true}
							/>
							<MessageDisplay label={t`Multi-line`} message={multiLineMessage} channel={mockChannel} isCompact={true} />
							<MessageDisplay label={t`Markdown`} message={markdownMessage} channel={mockChannel} isCompact={true} />
						</div>
					</div>
				</SettingsTabSection>
				<SettingsTabSection
					title={<Trans>Author Variations</Trans>}
					description={<Trans>Regular, bot, system, and timed out users rendered in cozy and compact modes.</Trans>}
				>
					<div className={styles.displayModeRow}>
						<div className={styles.displayModeColumn}>
							<div className={styles.displayModeHeader}>
								<Trans>Cozy Mode</Trans>
							</div>
							<MessageDisplay
								label={t`Regular user`}
								message={regularUserMessage}
								channel={mockChannel}
								isCompact={false}
							/>
							<MessageDisplay label={t`Bot user`} message={botUserMessage} channel={mockChannel} isCompact={false} />
							<MessageDisplay
								label={t`System user`}
								message={systemUserMessage}
								channel={mockChannel}
								isCompact={false}
							/>
							<MessageDisplay
								label={t`Timed out user`}
								message={timedOutUserMessage}
								channel={mockChannel}
								isCompact={false}
							/>
						</div>
						<div className={styles.displayModeColumn}>
							<div className={styles.displayModeHeader}>
								<Trans>Compact Mode</Trans>
							</div>
							<MessageDisplay
								label={t`Regular user`}
								message={regularUserMessage}
								channel={mockChannel}
								isCompact={true}
							/>
							<MessageDisplay label={t`Bot user`} message={botUserMessage} channel={mockChannel} isCompact={true} />
							<MessageDisplay
								label={t`System user`}
								message={systemUserMessage}
								channel={mockChannel}
								isCompact={true}
							/>
							<MessageDisplay
								label={t`Timed out user`}
								message={timedOutUserMessage}
								channel={mockChannel}
								isCompact={true}
							/>
						</div>
					</div>
				</SettingsTabSection>
				<SettingsTabSection
					title={<Trans>Embeds</Trans>}
					description={<Trans>Rich embeds with actual CDN thumbnails.</Trans>}
				>
					<div className={styles.messagesColumn}>
						<SubsectionTitle>
							<Trans>Rich Embed</Trans>
						</SubsectionTitle>
						<MessageDisplay
							label="With fields, author, footer"
							message={richEmbedMessage}
							channel={mockChannel}
							isCompact={false}
						/>
					</div>
				</SettingsTabSection>
				<SettingsTabSection
					title={<Trans>Attachment Mosaics</Trans>}
					description={<Trans>Layouts for 1-10 image attachments using CDN assets.</Trans>}
				>
					<div className={styles.mosaicGrid}>
						{attachmentMessages.map(({count, message}) => (
							<div key={count} className={styles.mosaicItem}>
								<div className={styles.mosaicLabel}>
									<Plural value={count} one="# image" other="# images" />
								</div>
								<div className={styles.previewCard}>
									<div className={styles.previewBody}>
										<Message
											channel={mockChannel}
											message={message}
											previewContext={MessagePreviewContext.SETTINGS}
											readonlyPreview={true}
											removeTopSpacing={true}
											compact={false}
											idPrefix="component-gallery-attachment-preview"
											behaviorOverrides={{
												...READONLY_MESSAGE_BEHAVIOR,
												messageDisplayCompact: false,
											}}
										/>
									</div>
								</div>
							</div>
						))}
					</div>
				</SettingsTabSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
};
